import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSimulationStore } from "@/lib/store";
import {
  PERSONA_IMAGES,
  MEDDPICC_LABELS,
  PHASE_INFO,
  getTrustLevel,
} from "@/lib/api";
import { getScenarioPersonas } from "@/lib/personaCatalog";
import { ChatPanel } from "@/components/simulation/ChatPanel";
import { VoiceChatPanel } from "@/components/simulation/VoiceChatPanel";
import { MetricsPanel } from "@/components/simulation/MetricsPanel";
import { CoachPanel } from "@/components/simulation/CoachPanel";
import { InterimSummaryPanel } from "@/components/simulation/InterimSummaryPanel";
import { CompletionPanel } from "@/components/simulation/CompletionPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  LogOut,
  BarChart3,
  Lightbulb,
  Users,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const SimulationPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const {
    dealState,
    messages,
    coachInterventions,
    loading,
    sending,
    simView,
    voiceMode,
    interimSummary,
    interimLoading,
    endReason,
    showEndDialog,
    latestCoachFeedback,
    initSession,
    handleSendMessage,
    handleShowInterim,
    handleSwitchPersona,
    handleEndSimulation,
    handleVoiceResponse,
    setVoiceMode,
    setShowEndDialog,
    continueCurrent,
    cleanup,
  } = useSimulationStore();

  useEffect(() => {
    initSession(sessionId).catch(() => {
      toast.error("Failed to load simulation state");
      navigate("/");
    });
    return () => cleanup();
  }, [sessionId, initSession, cleanup, navigate]);

  const onSendMessage = async (message) => {
    try {
      const result = await handleSendMessage(message);
      if (result?.phase_transitioned) {
        toast.success(`Phase Transition: ${PHASE_INFO[result.new_phase]?.name || result.new_phase}`, {
          description: "You've advanced to the next phase!",
        });
      }
    } catch {
      toast.error("Failed to send message");
    }
  };

  const onSwitchPersona = async (target) => {
    try {
      const name = await handleSwitchPersona(target);
      toast.success(`Now speaking with ${name}`, {
        description: "A new stakeholder has joined the conversation.",
      });
    } catch {
      toast.error("Failed to switch stakeholder");
    }
  };

  const onEndSim = async () => {
    try {
      await handleEndSimulation();
    } catch {
      toast.error("Failed to end simulation");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-400">Loading simulation...</p>
        </div>
      </div>
    );
  }

  const canSwitch = dealState?.is_active && (simView === "chat" || voiceMode) && (() => {
    const all = getScenarioPersonas(dealState?.scenario_id, dealState?.role_id);
    const available = all.filter(
      (p) => p !== dealState?.active_persona && !(dealState?.completed_personas || []).includes(p)
    );
    return available.length > 0 && (dealState?.session_clock || 0) >= 3;
  })();

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 flex-shrink-0 accent-border-top">
        <div className="px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base font-bold text-slate-900">{dealState?.role_id === "sdr" ? "Akamai SDR Lab" : "Akamai Sales Lab"}</h1>
              </div>
              <Badge
                className={`ml-1 text-xs ${
                  dealState?.phase === "P1" ? "phase-p1" :
                  dealState?.phase === "P2" ? "phase-p2" :
                  dealState?.phase === "P3" ? "phase-p3" :
                  dealState?.phase === "P4" ? "phase-p4" :
                  dealState?.phase === "COMPLETE" ? "phase-complete" :
                  "phase-failed"
                }`}
                data-testid="phase-badge"
              >
                {dealState?.phase_name || dealState?.phase}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              {/* Mobile panel toggles */}
              <div className="flex lg:hidden gap-1">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1 border-slate-200">
                      <BarChart3 className="w-4 h-4" />
                      <span className="hidden sm:inline">Metrics</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[300px] p-0">
                    <MetricsPanel dealState={dealState} />
                  </SheetContent>
                </Sheet>

                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1 border-slate-200 relative">
                      <Lightbulb className="w-4 h-4" />
                      <span className="hidden sm:inline">Coach</span>
                      {coachInterventions.filter((c) => !c.acknowledged).length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                          {coachInterventions.filter((c) => !c.acknowledged).length}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[320px] p-0">
                    <CoachPanel interventions={coachInterventions} latestFeedback={latestCoachFeedback} />
                  </SheetContent>
                </Sheet>
              </div>

              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-md border border-slate-100">
                <span className="text-xs text-slate-400">Turn</span>
                <span className="text-sm font-semibold text-slate-800" data-testid="turn-counter">
                  {dealState?.session_clock || 0}/{dealState?.max_turns || 40}
                </span>
              </div>

              {canSwitch && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShowInterim}
                  className="border-primary/30 text-primary hover:bg-primary/5"
                  data-testid="switch-stakeholder-btn"
                >
                  <Users className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Switch Stakeholder</span>
                  <span className="sm:hidden">Switch</span>
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEndDialog(true)}
                className="text-slate-500 hover:text-red-600 hover:border-red-200 border-slate-200"
                data-testid="end-simulation-btn"
              >
                <LogOut className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">End</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="simulation-shell h-full">
          <div className="hidden lg:block border-r border-slate-100 bg-white overflow-y-auto">
            <MetricsPanel dealState={dealState} />
          </div>

          <div className="flex flex-col bg-slate-50/50 overflow-hidden">
            {simView === "completed" ? (
              <CompletionPanel sessionId={sessionId} dealState={dealState} endReason={endReason} />
            ) : simView === "interim" ? (
              <InterimSummaryPanel
                summary={interimSummary}
                currentPersona={dealState?.active_persona}
                dealState={dealState}
                onSwitchPersona={onSwitchPersona}
                onContinue={continueCurrent}
                onEndSimulation={() => setShowEndDialog(true)}
                loading={interimLoading}
              />
            ) : voiceMode ? (
              <VoiceChatPanel
                dealState={dealState}
                messages={messages}
                onVoiceChatResponse={handleVoiceResponse}
                onSwitchToText={() => setVoiceMode(false)}
              />
            ) : (
              <ChatPanel
                messages={messages}
                onSendMessage={onSendMessage}
                sending={sending}
                dealState={dealState}
                onSwitchToVoice={() => setVoiceMode(true)}
              />
            )}
          </div>

          <div className="hidden lg:block border-l border-slate-100 bg-white overflow-y-auto">
            <CoachPanel interventions={coachInterventions} latestFeedback={latestCoachFeedback} />
          </div>
        </div>
      </div>

      <AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Simulation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end the simulation? You will be taken to your evaluation report. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Simulation</AlertDialogCancel>
            <AlertDialogAction onClick={onEndSim} className="bg-red-600 hover:bg-red-700" data-testid="confirm-end-btn">
              End & View Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
